import { type Reservation } from '../../Admin/Scheduling/model/SchedulingTypes';
import { type RocketRouteDetailFlightOfpCombo } from '../../hooks/data/rocketroute';
import { type ExternalDocument } from '../ExternalDocuments/ExternalDocuments';
import { type SupplementalDataAirport } from './Datasources/Airports';
import { type SupplementalDataMetar, type SupplementalDataTaf } from './Datasources/Taf';

export enum FlightSupplementalDataType {
    airport = 'airport',
    externalDocumentData = 'externalDocumentData',
    flightOrder = 'flightOrder',
    flightplan = 'flightplan',
    fuelPlan = 'fuelPlan',
    metar = 'metar',
    reservation = 'reservation',
    taf = 'taf',
}

export interface FlightSupplementalData {
    id: string;
    flightId?: string;
    legId?: string;
    reservationId?: string;
    reservationEventId?: string;
    usefulnessEndsByDate: Date;
    type: FlightSupplementalDataType;
}

export interface FlightSupplementalReservation extends FlightSupplementalData {
    type: FlightSupplementalDataType.reservation;
    reservation: Reservation;
}

export interface FlightSupplementalExternalDocumentData extends FlightSupplementalData {
    type: FlightSupplementalDataType.externalDocumentData;
    externalDocument: ExternalDocument;
}

export interface FlightSupplementalAirport extends FlightSupplementalData {
    type: FlightSupplementalDataType.airport;
    airport: SupplementalDataAirport;
}

export interface FlightSupplementalTaf extends FlightSupplementalData {
    type: FlightSupplementalDataType.taf;
    taf: SupplementalDataTaf;
}

export interface FlightSupplementalMetar extends FlightSupplementalData {
    type: FlightSupplementalDataType.metar;
    metar: SupplementalDataMetar;
}

export interface FlightSupplementalFlightplan extends FlightSupplementalData {
    type: FlightSupplementalDataType.flightplan;
    flightplan: RocketRouteDetailFlightOfpCombo;
}

/**
 * Flight order (pilot + duty day) trimmed down to what the schedule card needs: which legs it
 * covers, whether they are approved, and where to send the pilot to approve them. Snapshots are
 * deliberately not stored — everything they describe is already in the reservation.
 */
export interface SupplementalFlightOrder {
    id: string;
    /**
     * The pilot the order belongs to. A TLB is shared: until the next sync, the orders stored for the
     * previous crew member are still on the device, and only this tells them apart. Absent on a copy
     * stored before it was kept — such a copy belongs to nobody until it is re-read.
     */
    crewResourceId?: string;
    /** YYYY-MM-DD, local (Europe/Prague) duty day of the order's first leg. */
    flightDay: string;
    /** Unguessable token addressing the approval screen; without it there is nothing to open. */
    shareToken: string | null;
    status: string;
    /** When OPS handed the document to the pilot; an order without it is a draft and is not stored. */
    sentAt: string | null;
    /**
     * "x" while the pilot still owes an answer, null once the day is settled — the notifier's own
     * flag, and the only one that also counts legs taken out of the day. See needsConfirmation.
     */
    needsApproval?: string | null;
    legCount: number;
    approvedLegCount: number;
    /**
     * Legs the order covers, from snapshotRefs. `legId` is the reservation's ChildEvent id.
     * `status` is the pilot's own answer about that leg (ApprovalStatus), read only while the order
     * still owes one — absent means the order's counters answer for it. See getLegState.
     *
     * `removed` marks a leg the order no longer covers, unacknowledged. It has no snapshotRef, and
     * the reservation may still carry its ChildEvent — this list is the only record. See
     * withLegApprovals.
     */
    legs: Array<{ legId: string; removed?: boolean; role: string; status?: string }>;
    /**
     * The sentences of the order's last change notification, each on the leg it is about — the very
     * text the pilot was sent by e-mail and SMS, and the same one the approval page repeats under
     * the leg. Absent on copies stored before the texts were kept; the next supplemental sync fills
     * them in, and until then the order's counters answer for the leg.
     *
     * Stored WITHOUT the leg each sentence names (see withoutLegLabel, applied by the sync): they are
     * shown under the card of that leg, which states the date, the aircraft and the route already.
     */
    changes?: SupplementalFlightOrderChange[];
}

export type SupplementalFlightOrderChange = { legId: string; text: string };

/**
 * (#3029) Palivové VSTUPY letadla z uložené osy, uložené kvůli offline.
 *
 * Neukládají se hotová čísla, jen dvě hodnoty, ze kterých si pilotní řetěz (`byLegFuelData`)
 * dopočítá zbytek: uplift každého legu a seed rezervace. Ten uplift nese volbu dispečera, což je
 * jediné, co pilot z osy opravdu potřebuje — bloby by byly o dva řády větší a stejně by se
 * přepočítávaly lokálně.
 */
export interface SupplementalFuelPlan {
    aircraftId: string;
    /** `ChildEvent.id` → uplift v jednotce paliva profilu. */
    upliftByEventId: Record<string, number>;
    /** `Reservation.id` → FOB prvního legu rezervace, fallback seed řetězu. */
    seedByReservationId: Record<string, number>;
    /** (#3087) `ChildEvent.id` → FOB legu z osy; volitelné, starší záznamy ho nemají. */
    fobByEventId?: Record<string, number>;
    /** Kdy backend osu dopočítal (ISO) — stáří čísel patří i pilotovi. */
    computedAt: string | null;
}

export interface FlightSupplementalFuelPlan extends FlightSupplementalData {
    type: FlightSupplementalDataType.fuelPlan;
    fuelPlan: SupplementalFuelPlan;
}

export interface FlightSupplementalFlightOrder extends FlightSupplementalData {
    type: FlightSupplementalDataType.flightOrder;
    flightOrder: SupplementalFlightOrder;
}

export function flightSupplementalDataIsReservation(
    data: FlightSupplementalData
): data is FlightSupplementalReservation {
    return data.type === FlightSupplementalDataType.reservation;
}

export function flightSupplementalDataIsExternalDocumentData(
    data: FlightSupplementalData
): data is FlightSupplementalExternalDocumentData {
    return data.type === FlightSupplementalDataType.externalDocumentData;
}

export function flightSupplementalDataIsAirport(data: FlightSupplementalData): data is FlightSupplementalAirport {
    return data.type === FlightSupplementalDataType.airport;
}

export function flightSupplementalDataIsFuelPlan(data: FlightSupplementalData): data is FlightSupplementalFuelPlan {
    return data.type === FlightSupplementalDataType.fuelPlan;
}

export function flightSupplementalDataIsFlightOrder(
    data: FlightSupplementalData
): data is FlightSupplementalFlightOrder {
    return data.type === FlightSupplementalDataType.flightOrder;
}

export function flightSupplementalDataIsTaf(data: FlightSupplementalData): data is FlightSupplementalTaf {
    return data.type === FlightSupplementalDataType.taf;
}

export function flightSupplementalDataIsMetar(data: FlightSupplementalData): data is FlightSupplementalMetar {
    return data.type === FlightSupplementalDataType.metar;
}
